// Two-strike reveal for practice-set retries.
//
// The cued grader returns the hand-authored model answer (improvement) and the
// open grader returns two correct combinations (alternatives) — both copyable
// answers to the very sentences the kid is about to retry. Revealing them on
// the FIRST fail turns the retry into transcription, so: first fail shows the
// diagnosis checks only; example answers unlock from the second fail (worked-
// example teaching after genuine struggle); a pass is never stripped.
//
// A pass that comes after the reveal is imitation, not independent execution,
// so its recorded rating is capped at Strong — still a pass, but the row never
// claims "last: Excellent" off a copied model.

import type { EvalResult, SubRating } from "./types";

// `failedAttemptsBefore` counts retries the kid has already taken on this set
// (0 while judging the first submission).
export const shouldSuppressExamples = (failedAttemptsBefore: number, passed: boolean): boolean =>
  !passed && failedAttemptsBefore === 0;

export const stripExamples = (result: EvalResult): EvalResult => ({
  ...result,
  improvement: undefined,
  alternativesTitle: undefined,
  alternatives: undefined,
});

// Graduated (three-stage) reveal — for structured tools whose grader emits TWO
// granularities: a focused `improvement` (the single weakest part) and a full
// `alternatives[0]` model. Instead of dumping both on the second fail, a tool can
// reveal them in stages so a stuck writer gets a targeted nudge before the whole
// worked answer:
//   diagnosis   → checks only (first fail) — same as the two-strike default
//   improvement → checks + the focused weakest-part model; full model still hidden
//   full        → everything (on a pass, or when the writer asks to see it)
// `stripAlternatives` is the middle rung: keep the focused improvement, drop the
// full model. This is opt-in; tools using the plain two-strike reveal are unaffected.
export const stripAlternatives = (result: EvalResult): EvalResult => ({
  ...result,
  alternativesTitle: undefined,
  alternatives: undefined,
});

export type RevealStage = "diagnosis" | "improvement" | "full";

export const revealStage = (
  failedAttemptsBefore: number,
  passed: boolean,
  fullModelRequested: boolean,
): RevealStage => {
  if (passed) return "full";
  if (failedAttemptsBefore === 0) return "diagnosis";
  return fullModelRequested ? "full" : "improvement";
};

export const applyRevealStage = (result: EvalResult, stage: RevealStage): EvalResult => {
  if (stage === "diagnosis") return stripExamples(result);
  if (stage === "improvement") return stripAlternatives(result);
  return result;
};

// Examples were on screen iff some failed attempt rendered unsuppressed: the
// 2nd fail displays with failedAttemptsBefore=1, and its retry click bumps the
// counter to 2 before any later submission can pass.
export const examplesWereRevealed = (failedAttemptsBefore: number): boolean =>
  failedAttemptsBefore >= 2;

export const capRevealedPassRating = (rating: SubRating, revealed: boolean): SubRating =>
  revealed && rating === "Excellent" ? "Strong" : rating;

// A learner who submits text equal to one of the authored model answers must
// pass: the model is, by authorial assertion, a strong version, and the
// two-strike reveal explicitly invites copying it on the retry (the pass is then
// capped by capRevealedPassRating). The live grader is non-deterministic and has
// been seen to mark a copied model as below the bar — silently breaking that
// contract and telling the learner the suggestion we just showed them is wrong.
// This guard lets a runner short-circuit to a pass on an exact match. Matching is
// case- and punctuation-insensitive so trivial transcription differences (a
// dropped capital or full stop, smart vs straight quotes) still count, but a
// genuine edit (a different verb) does not — that is the learner's own answer and
// is graded normally.
const normalizeForModelMatch = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[.,!?;:"'“”‘’]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const matchesAuthoredModel = (answer: string, models: string[]): boolean => {
  const normalized = normalizeForModelMatch(answer);
  if (!normalized) return false;
  return models.some((m) => normalizeForModelMatch(m) === normalized);
};

// A universal synthetic pass for a verbatim copy of a revealed model. The single
// "Strong" check clears the weakest-link gate (isPass(weakestRating(checks))) AND
// tone "great" clears the holistic gate (tonePass), so one value works for every
// runner regardless of how it gates. Where a runner records a rating it still
// flows through capRevealedPassRating, which holds a post-reveal copy to Strong.
export const MODEL_MATCH_PASS: EvalResult = {
  verdict: "That's one of the strong versions — nicely done.",
  tone: "great",
  checks: [
    {
      label: "Strong version",
      rating: "Strong",
      body: "You've matched the kind of answer this practice was after.",
    },
  ],
};

// The model answers a grader hands back, which the feedback card reveals on a
// fail: the worked "improvement" example plus any listed "alternatives". Tools
// whose reveal is grader-returned (no static authored array) accumulate these
// across attempts and match a later submission against them. Returns [] when the
// grader supplied no model, so the guard is a harmless no-op in that case.
export const revealedModelsOf = (eval_: EvalResult | null | undefined): string[] =>
  eval_
    ? [eval_.improvement?.example, ...(eval_.alternatives ?? []).map((a) => a.example)].filter(
        (s): s is string => !!s,
      )
    : [];
